// ============================================================================
// ADMIN DATING - LISTE DES PROFILS (VERSION PRO)
// Gestion complète des profils utilisateurs du module Dating
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleSelect,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  Heart,
  Users,
  Search,
  MoreHorizontal,
  Eye,
  EyeOff,
  Ban,
  CheckCircle,
  XCircle,
  Crown,
  Shield,
  ShieldCheck,
  ShieldX,
  Trash2,
  ArrowLeft,
  Loader2,
  RefreshCw,
  Filter,
  Download,
  Mail,
  Calendar,
  MapPin,
  Phone,
  MessageCircle,
  Sparkles,
  TrendingUp,
  UserCheck,
  UserX,
  Grid3X3,
  List,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Edit,
  Send,
  Image,
  Clock,
  Activity,
  Zap,
  Star,
  AlertTriangle,
  MoreVertical,
  Plus
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useTenantContext } from '../../../context/TenantContext';
import { useToast } from '../../../components/ui/use-toast';
import { DatingProfile, DATING_GENDER_LABELS, DATING_LOOKING_FOR_LABELS, DATING_INTERESTS } from '../../../types/dating';
import { applyDomainFilterToQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';
import { format, formatDistanceToNow, differenceInYears, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';

// ============================================================================
// TYPES
// ============================================================================

type FilterStatus = 'all' | 'active' | 'inactive' | 'verified' | 'unverified' | 'banned' | 'vip' | 'online' | 'boosted';
type FilterGender = 'all' | 'male' | 'female' | 'non_binary' | 'other';
type SortField = 'created_at' | 'display_name' | 'total_matches' | 'profile_views' | 'last_active_at';
type SortDirection = 'asc' | 'desc';
type ViewMode = 'table' | 'grid';

interface DatingStats {
  total: number;
  active: number;
  verified: number;
  vip: number;
  banned: number;
  online: number;
  newToday: number;
  newThisWeek: number;
  totalMatches: number;
  avgMatchRate: number;
}

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

export default function DatingProfileList() {
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  const navigate = useNavigate();

  // États principaux
  const [profiles, setProfiles] = useState<DatingProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState<DatingStats | null>(null);

  // Filtres et recherche
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [filterGender, setFilterGender] = useState<FilterGender>('all');
  const [filterAgeMin, setFilterAgeMin] = useState<string>('');
  const [filterAgeMax, setFilterAgeMax] = useState<string>('');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Vue et sélection
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  // Modals
  const [selectedProfile, setSelectedProfile] = useState<DatingProfile | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showBulkActionDialog, setShowBulkActionDialog] = useState(false);
  const [bulkAction, setBulkAction] = useState<'ban' | 'verify' | 'delete' | null>(null);
  const [banReason, setBanReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // ============================================================================
  // CHARGEMENT DES DONNÉES
  // ============================================================================

  const fetchProfiles = async () => {
    if (!tenant?.id) return;

    setLoading(true);
    try {
      const { domain } = getDomainFilterParams();
      
      let query = supabase
        .from('dating_profiles')
        .select('*');

      // Appliquer le filtrage multi-tenant
      query = applyDomainFilterToQuery(query);
      
      // Tri
      query = query.order(sortField, { ascending: sortDirection === 'asc' });

      const { data, error } = await query;

      if (error) throw error;
      setProfiles(data as DatingProfile[]);
      
      // Calculer les statistiques
      if (data) {
        const today = new Date();
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        const todayStart = new Date(today.setHours(0, 0, 0, 0));
        
        const totalLikes = data.reduce((sum, p) => sum + (p.total_likes_received || 0), 0);
        const totalMatches = data.reduce((sum, p) => sum + (p.total_matches || 0), 0);
        
        setStats({
          total: data.length,
          active: data.filter(p => p.is_active && !p.is_banned).length,
          verified: data.filter(p => p.is_verified).length,
          vip: data.filter(p => p.is_vip).length,
          banned: data.filter(p => p.is_banned).length,
          online: data.filter(p => p.is_online).length,
          newToday: data.filter(p => new Date(p.created_at) >= todayStart).length,
          newThisWeek: data.filter(p => new Date(p.created_at) >= weekAgo).length,
          totalMatches,
          avgMatchRate: totalLikes > 0 ? Math.round((totalMatches / totalLikes) * 100) : 0
        });
      }
    } catch (error) {
      console.error('Erreur chargement profils:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les profils',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, [tenant, sortField, sortDirection]);

  // Reset page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus, filterGender, filterAgeMin, filterAgeMax]);

  // ============================================================================
  // FILTRAGE ET TRI
  // ============================================================================

  // Calculer l'âge
  const calculateAge = (birthDate: string): number => {
    if (!birthDate) return 0;
    return differenceInYears(new Date(), new Date(birthDate));
  };

  // Filtrer les profils
  const filteredProfiles = useMemo(() => {
    return profiles.filter(profile => {
      // Recherche textuelle
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          profile.display_name?.toLowerCase().includes(query) ||
          profile.email?.toLowerCase().includes(query) ||
          profile.city?.toLowerCase().includes(query) ||
          profile.phone?.toLowerCase().includes(query) ||
          profile.bio?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Filtre par statut
      switch (filterStatus) {
        case 'active':
          if (!profile.is_active || profile.is_banned) return false;
          break;
        case 'inactive':
          if (profile.is_active) return false;
          break;
        case 'verified':
          if (!profile.is_verified) return false;
          break;
        case 'unverified':
          if (profile.is_verified) return false;
          break;
        case 'banned':
          if (!profile.is_banned) return false;
          break;
        case 'vip':
          if (!profile.is_vip) return false;
          break;
        case 'online':
          if (!profile.is_online) return false;
          break;
        case 'boosted':
          if (!profile.is_boosted) return false;
          break;
      }

      // Filtre par genre
      if (filterGender !== 'all' && profile.gender !== filterGender) {
        return false;
      }

      // Filtre par âge
      if (profile.birth_date) {
        const age = calculateAge(profile.birth_date);
        if (filterAgeMin && age < parseInt(filterAgeMin)) return false;
        if (filterAgeMax && age > parseInt(filterAgeMax)) return false;
      }

      return true;
    });
  }, [profiles, searchQuery, filterStatus, filterGender, filterAgeMin, filterAgeMax]);

  // Pagination
  const totalPages = Math.ceil(filteredProfiles.length / itemsPerPage);
  const paginatedProfiles = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredProfiles.slice(start, start + itemsPerPage);
  }, [filteredProfiles, currentPage, itemsPerPage]);

  // Gestion de la sélection
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedProfiles([]);
    } else {
      setSelectedProfiles(paginatedProfiles.map(p => p.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectProfile = (profileId: string) => {
    setSelectedProfiles(prev => 
      prev.includes(profileId) 
        ? prev.filter(id => id !== profileId)
        : [...prev, profileId]
    );
  };

  // Tri
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // ============================================================================
  // ACTIONS
  // ============================================================================

  // Bannir un profil
  const handleBan = async () => {
    if (!selectedProfile) return;

    setProcessing(true);
    try {
      const { error } = await supabase
        .from('dating_profiles')
        .update({
          is_banned: true,
          ban_reason: banReason,
          banned_at: new Date().toISOString(),
          is_active: false,
          is_visible: false
        })
        .eq('id', selectedProfile.id);

      if (error) throw error;

      toast({
        title: 'Profil banni',
        description: `${selectedProfile.display_name} a été banni`
      });

      setShowBanDialog(false);
      setBanReason('');
      setSelectedProfile(null);
      fetchProfiles();
    } catch (error) {
      console.error('Erreur bannissement:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de bannir le profil',
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  // Débannir un profil
  const handleUnban = async (profile: DatingProfile) => {
    try {
      const { error } = await supabase
        .from('dating_profiles')
        .update({
          is_banned: false,
          ban_reason: null,
          banned_at: null,
          is_active: true
        })
        .eq('id', profile.id);

      if (error) throw error;

      toast({
        title: 'Profil débanni',
        description: `${profile.display_name} a été débanni`
      });

      fetchProfiles();
    } catch (error) {
      console.error('Erreur débannissement:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de débannir le profil',
        variant: 'destructive'
      });
    }
  };

  // Vérifier un profil
  const handleVerify = async (profile: DatingProfile) => {
    try {
      const { error } = await supabase
        .from('dating_profiles')
        .update({
          is_verified: true,
          verified_at: new Date().toISOString(),
          verification_method: 'admin'
        })
        .eq('id', profile.id);

      if (error) throw error;

      toast({
        title: 'Profil vérifié',
        description: `${profile.display_name} est maintenant vérifié`
      });

      fetchProfiles();
    } catch (error) {
      console.error('Erreur vérification:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de vérifier le profil',
        variant: 'destructive'
      });
    }
  };

  // Retirer la vérification
  const handleUnverify = async (profile: DatingProfile) => {
    try {
      const { error } = await supabase
        .from('dating_profiles')
        .update({
          is_verified: false,
          verified_at: null,
          verification_method: null
        })
        .eq('id', profile.id);

      if (error) throw error;

      toast({
        title: 'Vérification retirée',
        description: `${profile.display_name} n'est plus vérifié`
      });

      fetchProfiles();
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de retirer la vérification',
        variant: 'destructive'
      });
    }
  };

  // Activer/Désactiver un profil
  const handleToggleActive = async (profile: DatingProfile) => {
    try {
      const { error } = await supabase
        .from('dating_profiles')
        .update({
          is_active: !profile.is_active,
          is_visible: !profile.is_active
        })
        .eq('id', profile.id);

      if (error) throw error;

      toast({
        title: profile.is_active ? 'Profil désactivé' : 'Profil activé',
        description: `${profile.display_name} a été ${profile.is_active ? 'désactivé' : 'activé'}`
      });

      fetchProfiles();
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de modifier le statut',
        variant: 'destructive'
      });
    }
  };

  // Supprimer un profil
  const handleDelete = async () => {
    if (!selectedProfile) return;

    setProcessing(true);
    try {
      // Supprimer les données associées
      await supabase.from('dating_messages').delete().or(`sender_id.eq.${selectedProfile.id}`);
      await supabase.from('dating_conversations').delete().or(`participant1_id.eq.${selectedProfile.id},participant2_id.eq.${selectedProfile.id}`);
      await supabase.from('dating_matches').delete().or(`profile1_id.eq.${selectedProfile.id},profile2_id.eq.${selectedProfile.id}`);
      await supabase.from('dating_interactions').delete().or(`from_profile_id.eq.${selectedProfile.id},to_profile_id.eq.${selectedProfile.id}`);
      await supabase.from('dating_subscriptions').delete().eq('profile_id', selectedProfile.id);
      await supabase.from('dating_orders').delete().eq('profile_id', selectedProfile.id);
      
      // Supprimer le profil
      const { error } = await supabase
        .from('dating_profiles')
        .delete()
        .eq('id', selectedProfile.id);

      if (error) throw error;

      toast({
        title: 'Profil supprimé',
        description: `${selectedProfile.display_name} a été supprimé définitivement`
      });

      setShowDeleteDialog(false);
      setSelectedProfile(null);
      fetchProfiles();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le profil',
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  // Actions groupées
  const handleBulkAction = async () => {
    if (selectedProfiles.length === 0 || !bulkAction) return;

    setProcessing(true);
    try {
      if (bulkAction === 'ban') {
        const { error } = await supabase
          .from('dating_profiles')
          .update({
            is_banned: true,
            ban_reason: banReason || 'Action groupée admin',
            banned_at: new Date().toISOString(),
            is_active: false,
            is_visible: false
          })
          .in('id', selectedProfiles);

        if (error) throw error;
        toast({
          title: 'Profils bannis',
          description: `${selectedProfiles.length} profil(s) ont été bannis`
        });
      } else if (bulkAction === 'verify') {
        const { error } = await supabase
          .from('dating_profiles')
          .update({
            is_verified: true,
            verified_at: new Date().toISOString(),
            verification_method: 'admin_bulk'
          })
          .in('id', selectedProfiles);

        if (error) throw error;
        toast({
          title: 'Profils vérifiés',
          description: `${selectedProfiles.length} profil(s) ont été vérifiés`
        });
      } else if (bulkAction === 'delete') {
        for (const profileId of selectedProfiles) {
          await supabase.from('dating_messages').delete().or(`sender_id.eq.${profileId}`);
          await supabase.from('dating_conversations').delete().or(`participant1_id.eq.${profileId},participant2_id.eq.${profileId}`);
          await supabase.from('dating_matches').delete().or(`profile1_id.eq.${profileId},profile2_id.eq.${profileId}`);
          await supabase.from('dating_interactions').delete().or(`from_profile_id.eq.${profileId},to_profile_id.eq.${profileId}`);
          await supabase.from('dating_subscriptions').delete().eq('profile_id', profileId);
          await supabase.from('dating_orders').delete().eq('profile_id', profileId);
        }
        
        const { error } = await supabase
          .from('dating_profiles')
          .delete()
          .in('id', selectedProfiles);

        if (error) throw error;
        toast({
          title: 'Profils supprimés',
          description: `${selectedProfiles.length} profil(s) ont été supprimés`
        });
      }

      setShowBulkActionDialog(false);
      setBulkAction(null);
      setBanReason('');
      setSelectedProfiles([]);
      setSelectAll(false);
      fetchProfiles();
    } catch (error) {
      console.error('Erreur action groupée:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'effectuer l\'action groupée',
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    const headers = ['Nom', 'Email', 'Téléphone', 'Genre', 'Âge', 'Ville', 'Pays', 'VIP', 'Vérifié', 'Actif', 'Banni', 'Matches', 'Vues', 'Inscription'];
    const rows = filteredProfiles.map(p => [
      p.display_name || '',
      p.email || '',
      p.phone || '',
      DATING_GENDER_LABELS[p.gender] || '',
      p.birth_date ? calculateAge(p.birth_date).toString() : '',
      p.city || '',
      p.country || '',
      p.is_vip ? 'Oui' : 'Non',
      p.is_verified ? 'Oui' : 'Non',
      p.is_active ? 'Oui' : 'Non',
      p.is_banned ? 'Oui' : 'Non',
      (p.total_matches || 0).toString(),
      (p.profile_views || 0).toString(),
      format(new Date(p.created_at), 'dd/MM/yyyy', { locale: fr })
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dating_profiles_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    toast({
      title: 'Export réussi',
      description: `${filteredProfiles.length} profil(s) exporté(s)`
    });
  };

  // Rafraîchir
  const handleRefresh = () => {
    setRefreshing(true);
    fetchProfiles();
  };

  // ============================================================================
  // RENDU
  // ============================================================================

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/admin/dating">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl">
                  <Users className="w-6 h-6 text-white" />
                </div>
                Profils Dating
              </h1>
              <p className="text-gray-400 mt-1">
                {filteredProfiles.length} profil{filteredProfiles.length > 1 ? 's' : ''} sur {profiles.length}
                {selectedProfiles.length > 0 && (
                  <span className="ml-2 text-pink-400">• {selectedProfiles.length} sélectionné(s)</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Actions groupées */}
            {selectedProfiles.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="border-pink-500/50 text-pink-400 hover:bg-pink-500/10">
                    <Zap className="w-4 h-4 mr-2" />
                    Actions ({selectedProfiles.length})
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-gray-900 border-white/10">
                  <DropdownMenuItem 
                    onClick={() => { setBulkAction('verify'); setShowBulkActionDialog(true); }}
                    className="text-blue-400 hover:bg-blue-500/10"
                  >
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Vérifier tous
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => { setBulkAction('ban'); setShowBulkActionDialog(true); }}
                    className="text-orange-400 hover:bg-orange-500/10"
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    Bannir tous
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem 
                    onClick={() => { setBulkAction('delete'); setShowBulkActionDialog(true); }}
                    className="text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer tous
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            <Button variant="outline" onClick={handleExportCSV} className="border-white/10 text-gray-300 hover:bg-white/5">
              <Download className="w-4 h-4 mr-2" />
              Exporter
            </Button>
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-white/10 text-gray-300 hover:bg-white/5"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <div className="flex items-center border border-white/10 rounded-lg overflow-hidden">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setViewMode('table')}
                className={`rounded-none ${viewMode === 'table' ? 'bg-white/10 text-white' : 'text-gray-400'}`}
              >
                <List className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setViewMode('grid')}
                className={`rounded-none ${viewMode === 'grid' ? 'bg-white/10 text-white' : 'text-gray-400'}`}
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Statistiques */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            <Card className="bg-gradient-to-br from-pink-500/10 to-rose-500/10 border-pink-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Total</p>
                    <p className="text-2xl font-bold text-white">{stats.total}</p>
                    <p className="text-xs text-pink-400">+{stats.newToday} aujourd'hui</p>
                  </div>
                  <Users className="w-8 h-8 text-pink-500/50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Actifs</p>
                    <p className="text-2xl font-bold text-white">{stats.active}</p>
                    <p className="text-xs text-green-400">{stats.online} en ligne</p>
                  </div>
                  <Activity className="w-8 h-8 text-green-500/50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Vérifiés</p>
                    <p className="text-2xl font-bold text-white">{stats.verified}</p>
                    <p className="text-xs text-blue-400">{Math.round((stats.verified / stats.total) * 100) || 0}%</p>
                  </div>
                  <ShieldCheck className="w-8 h-8 text-blue-500/50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-yellow-500/10 to-amber-500/10 border-yellow-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">VIP</p>
                    <p className="text-2xl font-bold text-white">{stats.vip}</p>
                    <p className="text-xs text-yellow-400">Premium</p>
                  </div>
                  <Crown className="w-8 h-8 text-yellow-500/50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-500/10 to-rose-500/10 border-red-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Matches</p>
                    <p className="text-2xl font-bold text-white">{stats.totalMatches}</p>
                    <p className="text-xs text-red-400">Taux: {stats.avgMatchRate}%</p>
                  </div>
                  <Heart className="w-8 h-8 text-red-500/50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filtres avancés */}
        <Card className="bg-white/5 backdrop-blur-sm border border-white/10">
          <CardContent className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
              {/* Recherche */}
              <div className="lg:col-span-2 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par nom, email, ville, bio..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400"
                />
              </div>
              
              {/* Filtre Statut */}
              <SimpleSelect
                value={filterStatus}
                onValueChange={(v) => setFilterStatus(v as FilterStatus)}
                options={[
                  { value: 'all', label: 'Tous les statuts' },
                  { value: 'active', label: '✅ Actifs' },
                  { value: 'inactive', label: '⏸️ Inactifs' },
                  { value: 'verified', label: '🛡️ Vérifiés' },
                  { value: 'unverified', label: '❓ Non vérifiés' },
                  { value: 'vip', label: '👑 VIP' },
                  { value: 'online', label: '🟢 En ligne' },
                  { value: 'boosted', label: '⚡ Boostés' },
                  { value: 'banned', label: '🚫 Bannis' },
                ]}
              />
              
              {/* Filtre Genre */}
              <SimpleSelect
                value={filterGender}
                onValueChange={(v) => setFilterGender(v as FilterGender)}
                options={[
                  { value: 'all', label: 'Tous les genres' },
                  { value: 'male', label: '👨 Hommes' },
                  { value: 'female', label: '👩 Femmes' },
                  { value: 'non_binary', label: '🌈 Non-binaire' },
                  { value: 'other', label: '❓ Autre' },
                ]}
              />
              
              {/* Filtre Âge */}
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="Âge min"
                  value={filterAgeMin}
                  onChange={(e) => setFilterAgeMin(e.target.value)}
                  className="bg-white/5 border-white/10 text-white placeholder-gray-400 w-20"
                  min="18"
                  max="99"
                />
                <span className="text-gray-400">-</span>
                <Input
                  type="number"
                  placeholder="Âge max"
                  value={filterAgeMax}
                  onChange={(e) => setFilterAgeMax(e.target.value)}
                  className="bg-white/5 border-white/10 text-white placeholder-gray-400 w-20"
                  min="18"
                  max="99"
                />
              </div>
              
              {/* Items par page */}
              <SimpleSelect
                value={itemsPerPage.toString()}
                onValueChange={(v) => setItemsPerPage(parseInt(v))}
                options={[
                  { value: '10', label: '10 par page' },
                  { value: '20', label: '20 par page' },
                  { value: '50', label: '50 par page' },
                  { value: '100', label: '100 par page' },
                ]}
              />
            </div>
          </CardContent>
        </Card>

        {/* Contenu principal */}
        <Card className="bg-white/5 backdrop-blur-sm border border-white/10">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-10 h-10 animate-spin text-pink-500 mb-4" />
                <p className="text-gray-400">Chargement des profils...</p>
              </div>
            ) : filteredProfiles.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-pink-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-pink-500" />
                </div>
                <h3 className="text-lg font-medium text-white mb-2">Aucun profil trouvé</h3>
                <p className="text-gray-400 mb-4">Modifiez vos filtres ou attendez de nouvelles inscriptions</p>
                <Button variant="outline" onClick={() => {
                  setSearchQuery('');
                  setFilterStatus('all');
                  setFilterGender('all');
                  setFilterAgeMin('');
                  setFilterAgeMax('');
                }}>
                  Réinitialiser les filtres
                </Button>
              </div>
            ) : viewMode === 'table' ? (
              /* Vue Table */
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-white/10 hover:bg-transparent">
                      <TableHead className="w-12">
                        <Checkbox 
                          checked={selectAll}
                          onCheckedChange={handleSelectAll}
                          className="border-white/30"
                        />
                      </TableHead>
                      <TableHead className="text-xs font-medium text-gray-400">
                        <button onClick={() => handleSort('display_name')} className="flex items-center gap-1 hover:text-white">
                          Profil
                          <ArrowUpDown className="w-3 h-3" />
                        </button>
                      </TableHead>
                      <TableHead className="text-xs font-medium text-gray-400">Âge / Genre</TableHead>
                      <TableHead className="text-xs font-medium text-gray-400">Localisation</TableHead>
                      <TableHead className="text-xs font-medium text-gray-400">Statut</TableHead>
                      <TableHead className="text-xs font-medium text-gray-400">
                        <button onClick={() => handleSort('total_matches')} className="flex items-center gap-1 hover:text-white">
                          Stats
                          <ArrowUpDown className="w-3 h-3" />
                        </button>
                      </TableHead>
                      <TableHead className="text-xs font-medium text-gray-400">
                        <button onClick={() => handleSort('last_active_at')} className="flex items-center gap-1 hover:text-white">
                          Activité
                          <ArrowUpDown className="w-3 h-3" />
                        </button>
                      </TableHead>
                      <TableHead className="text-xs font-medium text-gray-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedProfiles.map((profile) => (
                      <TableRow 
                        key={profile.id} 
                        className={`hover:bg-white/5 border-b border-white/5 ${selectedProfiles.includes(profile.id) ? 'bg-pink-500/10' : ''}`}
                      >
                        <TableCell>
                          <Checkbox 
                            checked={selectedProfiles.includes(profile.id)}
                            onCheckedChange={() => handleSelectProfile(profile.id)}
                            className="border-white/30"
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center overflow-hidden ring-2 ring-white/10">
                                {profile.avatar_url ? (
                                  <img 
                                    src={profile.avatar_url} 
                                    alt={profile.display_name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span className="text-white font-bold text-lg">
                                    {profile.display_name?.charAt(0).toUpperCase()}
                                  </span>
                                )}
                              </div>
                              {profile.is_online && (
                                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-gray-900" />
                              )}
                            </div>
                            <div>
                              <div className="font-medium text-white flex items-center gap-2">
                                {profile.display_name}
                                {profile.is_vip && <Crown className="w-4 h-4 text-yellow-500" />}
                                {profile.is_verified && <ShieldCheck className="w-4 h-4 text-blue-500" />}
                                {profile.is_boosted && <Zap className="w-4 h-4 text-purple-500" />}
                              </div>
                              <div className="text-sm text-gray-400">{profile.email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="font-medium text-white">{profile.birth_date ? calculateAge(profile.birth_date) : '?'} ans</span>
                            <div className="text-sm text-gray-400">{DATING_GENDER_LABELS[profile.gender]}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-gray-300">
                            <MapPin className="w-4 h-4 text-gray-500" />
                            <span className="truncate max-w-[120px]">{profile.city || profile.country || 'Non renseigné'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {profile.is_banned ? (
                              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">🚫 Banni</Badge>
                            ) : profile.is_active ? (
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">✅ Actif</Badge>
                            ) : (
                              <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">⏸️ Inactif</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm space-y-1">
                            <div className="flex items-center gap-1 text-pink-400">
                              <Heart className="w-3 h-3" />
                              {profile.total_matches || 0}
                            </div>
                            <div className="flex items-center gap-1 text-gray-400">
                              <Eye className="w-3 h-3" />
                              {profile.profile_views || 0}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div className="text-gray-300">
                              {profile.last_active_at 
                                ? formatDistanceToNow(new Date(profile.last_active_at), { addSuffix: true, locale: fr })
                                : 'Jamais'
                              }
                            </div>
                            <div className="text-xs text-gray-500">
                              Inscrit {format(new Date(profile.created_at), 'dd/MM/yy', { locale: fr })}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-pink-400 hover:text-pink-300 hover:bg-pink-500/10"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-gray-900 border-white/10 w-48">
                              <DropdownMenuItem 
                                onClick={() => { setSelectedProfile(profile); setShowDetailModal(true); }}
                                className="text-gray-300 hover:bg-white/5"
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Voir les détails
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-white/10" />
                              {profile.is_verified ? (
                                <DropdownMenuItem onClick={() => handleUnverify(profile)} className="text-gray-300 hover:bg-white/5">
                                  <ShieldX className="w-4 h-4 mr-2" />
                                  Retirer vérification
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handleVerify(profile)} className="text-blue-400 hover:bg-blue-500/10">
                                  <ShieldCheck className="w-4 h-4 mr-2" />
                                  Vérifier
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleToggleActive(profile)} className="text-gray-300 hover:bg-white/5">
                                {profile.is_active ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                                {profile.is_active ? 'Désactiver' : 'Activer'}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-white/10" />
                              {profile.is_banned ? (
                                <DropdownMenuItem onClick={() => handleUnban(profile)} className="text-green-400 hover:bg-green-500/10">
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Débannir
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem 
                                  onClick={() => { setSelectedProfile(profile); setShowBanDialog(true); }}
                                  className="text-orange-400 hover:bg-orange-500/10"
                                >
                                  <Ban className="w-4 h-4 mr-2" />
                                  Bannir
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem 
                                onClick={() => { setSelectedProfile(profile); setShowDeleteDialog(true); }}
                                className="text-red-400 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              /* Vue Grille */
              <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {paginatedProfiles.map((profile) => (
                  <div 
                    key={profile.id}
                    className={`relative bg-white/5 rounded-xl border border-white/10 overflow-hidden hover:border-pink-500/50 transition-all cursor-pointer ${selectedProfiles.includes(profile.id) ? 'ring-2 ring-pink-500' : ''}`}
                    onClick={() => { setSelectedProfile(profile); setShowDetailModal(true); }}
                  >
                    {/* Checkbox */}
                    <div className="absolute top-3 left-3 z-10" onClick={(e) => e.stopPropagation()}>
                      <Checkbox 
                        checked={selectedProfiles.includes(profile.id)}
                        onCheckedChange={() => handleSelectProfile(profile.id)}
                        className="border-white/50 bg-black/50"
                      />
                    </div>
                    
                    {/* Badges */}
                    <div className="absolute top-3 right-3 z-10 flex gap-1">
                      {profile.is_vip && <Badge className="bg-yellow-500/90 text-black text-xs"><Crown className="w-3 h-3" /></Badge>}
                      {profile.is_verified && <Badge className="bg-blue-500/90 text-white text-xs"><ShieldCheck className="w-3 h-3" /></Badge>}
                      {profile.is_banned && <Badge className="bg-red-500/90 text-white text-xs">Banni</Badge>}
                    </div>
                    
                    {/* Photo */}
                    <div className="relative h-48 bg-gradient-to-br from-pink-500/20 to-rose-500/20">
                      {profile.avatar_url ? (
                        <img src={profile.avatar_url} alt={profile.display_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-6xl font-bold text-white/30">{profile.display_name?.charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                      {profile.is_online && (
                        <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                          <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                          En ligne
                        </div>
                      )}
                    </div>
                    
                    {/* Infos */}
                    <div className="p-4">
                      <h3 className="font-semibold text-white truncate">{profile.display_name}</h3>
                      <p className="text-sm text-gray-400">
                        {profile.birth_date ? calculateAge(profile.birth_date) : '?'} ans • {DATING_GENDER_LABELS[profile.gender]}
                      </p>
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                        <MapPin className="w-3 h-3" />
                        {profile.city || profile.country || 'Non renseigné'}
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
                        <div className="flex items-center gap-3 text-xs">
                          <span className="flex items-center gap-1 text-pink-400">
                            <Heart className="w-3 h-3" /> {profile.total_matches || 0}
                          </span>
                          <span className="flex items-center gap-1 text-gray-400">
                            <Eye className="w-3 h-3" /> {profile.profile_views || 0}
                          </span>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-pink-400 hover:text-pink-300 hover:bg-pink-500/10"
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-gray-900 border-white/10">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleVerify(profile); }} className="text-blue-400">
                              <ShieldCheck className="w-4 h-4 mr-2" /> Vérifier
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedProfile(profile); setShowBanDialog(true); }} className="text-orange-400">
                              <Ban className="w-4 h-4 mr-2" /> Bannir
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedProfile(profile); setShowDeleteDialog(true); }} className="text-red-400">
                              <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-400">
              Affichage {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredProfiles.length)} sur {filteredProfiles.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="border-white/10"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(pageNum)}
                      className={currentPage === pageNum ? "bg-pink-500 hover:bg-pink-600" : "border-white/10"}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="border-white/10"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Modal Détails du profil */}
        {showDetailModal && selectedProfile && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gradient-to-br from-gray-900 to-gray-950 rounded-2xl border border-white/10 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header du modal */}
              <div className="sticky top-0 bg-gray-900/95 backdrop-blur-sm border-b border-white/10 p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center overflow-hidden">
                      {selectedProfile.avatar_url ? (
                        <img src={selectedProfile.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-2xl font-bold text-white">{selectedProfile.display_name?.charAt(0)}</span>
                      )}
                    </div>
                    {selectedProfile.is_online && (
                      <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-900" />
                    )}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                      {selectedProfile.display_name}
                      {selectedProfile.is_vip && <Crown className="w-5 h-5 text-yellow-500" />}
                      {selectedProfile.is_verified && <ShieldCheck className="w-5 h-5 text-blue-500" />}
                    </h2>
                    <p className="text-gray-400">{selectedProfile.email}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-white">
                  <XCircle className="w-6 h-6" />
                </Button>
              </div>

              {/* Contenu du modal */}
              <div className="p-6 space-y-6">
                {/* Statistiques rapides */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white/5 rounded-xl p-4 text-center">
                    <Heart className="w-6 h-6 text-pink-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{selectedProfile.total_matches || 0}</p>
                    <p className="text-xs text-gray-400">Matches</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 text-center">
                    <Eye className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{selectedProfile.profile_views || 0}</p>
                    <p className="text-xs text-gray-400">Vues profil</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 text-center">
                    <Star className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{selectedProfile.total_likes_received || 0}</p>
                    <p className="text-xs text-gray-400">Likes reçus</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 text-center">
                    <MessageCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{selectedProfile.total_super_likes_received || 0}</p>
                    <p className="text-xs text-gray-400">Super Likes</p>
                  </div>
                </div>

                {/* Informations détaillées */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Infos personnelles */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Users className="w-5 h-5 text-pink-500" />
                      Informations personnelles
                    </h3>
                    <div className="bg-white/5 rounded-xl p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Âge</span>
                        <span className="text-white">{selectedProfile.birth_date ? `${calculateAge(selectedProfile.birth_date)} ans` : 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Genre</span>
                        <span className="text-white">{DATING_GENDER_LABELS[selectedProfile.gender]}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Recherche</span>
                        <span className="text-white">{DATING_LOOKING_FOR_LABELS[selectedProfile.looking_for]}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Localisation</span>
                        <span className="text-white">{selectedProfile.city}, {selectedProfile.country}</span>
                      </div>
                      {selectedProfile.phone && (
                        <div className="flex justify-between">
                          <span className="text-gray-400">Téléphone</span>
                          <span className="text-white">{selectedProfile.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Statuts */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Activity className="w-5 h-5 text-green-500" />
                      Statuts
                    </h3>
                    <div className="bg-white/5 rounded-xl p-4 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Compte</span>
                        {selectedProfile.is_active ? (
                          <Badge className="bg-green-500/20 text-green-400">Actif</Badge>
                        ) : (
                          <Badge className="bg-gray-500/20 text-gray-400">Inactif</Badge>
                        )}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Vérification</span>
                        {selectedProfile.is_verified ? (
                          <Badge className="bg-blue-500/20 text-blue-400">Vérifié</Badge>
                        ) : (
                          <Badge className="bg-gray-500/20 text-gray-400">Non vérifié</Badge>
                        )}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Abonnement</span>
                        {selectedProfile.is_vip ? (
                          <Badge className="bg-yellow-500/20 text-yellow-400">VIP</Badge>
                        ) : (
                          <Badge className="bg-gray-500/20 text-gray-400">Gratuit</Badge>
                        )}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Bannissement</span>
                        {selectedProfile.is_banned ? (
                          <Badge className="bg-red-500/20 text-red-400">Banni</Badge>
                        ) : (
                          <Badge className="bg-green-500/20 text-green-400">OK</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bio */}
                {selectedProfile.bio && (
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-white">Bio</h3>
                    <div className="bg-white/5 rounded-xl p-4">
                      <p className="text-gray-300 whitespace-pre-wrap">{selectedProfile.bio}</p>
                    </div>
                  </div>
                )}

                {/* Intérêts */}
                {selectedProfile.interests && selectedProfile.interests.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-white">Intérêts</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedProfile.interests.map((interest, i) => (
                        <Badge key={i} variant="outline" className="border-pink-500/50 text-pink-400">
                          {interest}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Dates */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-gray-400">Inscription</p>
                    <p className="text-white">{format(new Date(selectedProfile.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-gray-400">Dernière activité</p>
                    <p className="text-white">
                      {selectedProfile.last_active_at 
                        ? format(new Date(selectedProfile.last_active_at), 'dd/MM/yyyy HH:mm', { locale: fr })
                        : 'Jamais'
                      }
                    </p>
                  </div>
                  {selectedProfile.verified_at && (
                    <div className="bg-white/5 rounded-lg p-3">
                      <p className="text-gray-400">Vérifié le</p>
                      <p className="text-white">{format(new Date(selectedProfile.verified_at), 'dd/MM/yyyy', { locale: fr })}</p>
                    </div>
                  )}
                  {selectedProfile.banned_at && (
                    <div className="bg-red-500/10 rounded-lg p-3">
                      <p className="text-red-400">Banni le</p>
                      <p className="text-white">{format(new Date(selectedProfile.banned_at), 'dd/MM/yyyy', { locale: fr })}</p>
                      {selectedProfile.ban_reason && <p className="text-xs text-red-300 mt-1">{selectedProfile.ban_reason}</p>}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10">
                  {!selectedProfile.is_verified && (
                    <Button onClick={() => { handleVerify(selectedProfile); setShowDetailModal(false); }} className="bg-blue-500 hover:bg-blue-600">
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      Vérifier le profil
                    </Button>
                  )}
                  {selectedProfile.is_banned ? (
                    <Button onClick={() => { handleUnban(selectedProfile); setShowDetailModal(false); }} className="bg-green-500 hover:bg-green-600">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Débannir
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      onClick={() => { setShowDetailModal(false); setShowBanDialog(true); }}
                      className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      Bannir
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    onClick={() => { setShowDetailModal(false); setShowDeleteDialog(true); }}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dialog Bannissement */}
        {showBanDialog && selectedProfile && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gradient-to-br from-gray-900 to-gray-950 rounded-2xl border border-orange-500/30 max-w-md w-full">
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-orange-500/10 rounded-full">
                    <Ban className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Bannir ce profil ?</h3>
                    <p className="text-sm text-gray-400">{selectedProfile.display_name}</p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-gray-300">Ce profil ne pourra plus accéder au service Dating jusqu'à ce qu'il soit débanni.</p>
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">Raison du bannissement</label>
                  <Textarea
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    placeholder="Ex: Comportement inapproprié, faux profil..."
                    className="bg-white/5 border-white/10 text-white placeholder-gray-500"
                    rows={3}
                  />
                </div>
              </div>
              <div className="p-6 border-t border-white/10 flex justify-end gap-3">
                <Button variant="outline" onClick={() => { setShowBanDialog(false); setBanReason(''); }} className="border-white/10">
                  Annuler
                </Button>
                <Button onClick={handleBan} disabled={processing} className="bg-orange-500 hover:bg-orange-600">
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Ban className="w-4 h-4 mr-2" />}
                  Bannir
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Dialog Suppression */}
        {showDeleteDialog && selectedProfile && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gradient-to-br from-gray-900 to-gray-950 rounded-2xl border border-red-500/30 max-w-md w-full">
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-red-500/10 rounded-full">
                    <Trash2 className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Supprimer ce profil ?</h3>
                    <p className="text-sm text-gray-400">{selectedProfile.display_name}</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                  <p className="text-red-400 text-sm flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>Cette action est <strong>irréversible</strong>. Toutes les données du profil seront supprimées : matches, messages, conversations, abonnements.</span>
                  </p>
                </div>
                <p className="text-gray-300">Êtes-vous sûr de vouloir supprimer définitivement le profil de <strong className="text-white">{selectedProfile.display_name}</strong> ?</p>
              </div>
              <div className="p-6 border-t border-white/10 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="border-white/10">
                  Annuler
                </Button>
                <Button onClick={handleDelete} disabled={processing} className="bg-red-500 hover:bg-red-600">
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Supprimer définitivement
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Dialog Actions groupées */}
        {showBulkActionDialog && bulkAction && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gradient-to-br from-gray-900 to-gray-950 rounded-2xl border border-white/10 max-w-md w-full">
              <div className="p-6 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white">
                  {bulkAction === 'verify' && 'Vérifier les profils sélectionnés'}
                  {bulkAction === 'ban' && 'Bannir les profils sélectionnés'}
                  {bulkAction === 'delete' && 'Supprimer les profils sélectionnés'}
                </h3>
                <p className="text-sm text-gray-400">{selectedProfiles.length} profil(s) sélectionné(s)</p>
              </div>
              <div className="p-6 space-y-4">
                {bulkAction === 'ban' && (
                  <div>
                    <label className="text-sm font-medium text-gray-300 mb-2 block">Raison du bannissement</label>
                    <Textarea
                      value={banReason}
                      onChange={(e) => setBanReason(e.target.value)}
                      placeholder="Raison commune pour tous les profils..."
                      className="bg-white/5 border-white/10 text-white"
                      rows={3}
                    />
                  </div>
                )}
                {bulkAction === 'delete' && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <p className="text-red-400 text-sm flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                      Cette action est irréversible. Toutes les données des profils seront supprimées.
                    </p>
                  </div>
                )}
              </div>
              <div className="p-6 border-t border-white/10 flex justify-end gap-3">
                <Button variant="outline" onClick={() => { setShowBulkActionDialog(false); setBulkAction(null); setBanReason(''); }} className="border-white/10">
                  Annuler
                </Button>
                <Button 
                  onClick={handleBulkAction} 
                  disabled={processing}
                  className={
                    bulkAction === 'verify' ? 'bg-blue-500 hover:bg-blue-600' :
                    bulkAction === 'ban' ? 'bg-orange-500 hover:bg-orange-600' :
                    'bg-red-500 hover:bg-red-600'
                  }
                >
                  {processing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {bulkAction === 'verify' && 'Vérifier tous'}
                  {bulkAction === 'ban' && 'Bannir tous'}
                  {bulkAction === 'delete' && 'Supprimer tous'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
